/**
 * A top-bar entry that opens a wide panel of columns (Invest, More): each column a heading, an optional
 * picture card and its pages, drawn with the notification badge. It shows as active while the current page is one of its pages', closes on an outside click, Escape (focus back to the button) or
 * a route change, and isn't there at all when it has no pages. The panel is as wide as its columns but
 * never wider than the window, and shifts left to stay on screen when its button sits near the right edge.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { navigate, usePath } from '../lib/router';
import { GlyphBadge, type GlyphName } from './NotificationIcon';
import { cx, type IconName } from './ui';

/** A page in the top bar or the phone menu. */
export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Paths that belong to this item (its own pages and anything under them). */
  prefixes: string[];
}

export const within = (path: string, p: string) => path === p || path.startsWith(p + '/');

export interface MegaLink {
  to: string;
  label: string;
  blurb: string;
  glyph: GlyphName;
  /** Paths that belong to this page (its own and anything under them). */
  prefixes: string[];
}

/** A picture card heading a column: a generated image under public/menu, a title and a call to action. */
export interface Promo {
  to: string;
  image: string;
  title: string;
  cta: string;
  /** Colour of the call-to-action pill, matching the glow in the picture. */
  tone: 'accent' | 'up' | 'warn' | 'managed';
}

export interface MegaColumn {
  title: string;
  promo?: Promo;
  links: MegaLink[];
}

/** Fixed colours, not theme tokens: the pictures are dark in both themes. */
const TONE: Record<Promo['tone'], string> = {
  accent: 'bg-[#3d5cf5] text-white',
  up: 'bg-[#1fc7a0] text-[#05211a]',
  warn: 'bg-[#f5a524] text-[#1f1203]',
  managed: 'bg-[#b58cff] text-[#160a2e]',
};

/** The picture card, shared with the phone menu. */
export function PromoCard({ promo, onClick, className }: { promo: Promo; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx('group relative block aspect-video w-full overflow-hidden rounded-xl bg-[#0b0c0f] text-left text-white', className)}
    >
      <img src={promo.image} alt="" loading="lazy" decoding="async" className="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
      {/* Keeps the white title readable over the brighter part of a picture. */}
      <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      <span className="absolute right-3 bottom-3 left-3 flex flex-col items-start gap-2">
        <span className="text-[16px] leading-tight font-semibold">{promo.title}</span>
        <span className={cx('rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold', TONE[promo.tone])}>{promo.cta}</span>
      </span>
    </button>
  );
}

export function MegaMenu({ label, columns }: { label: string; columns: MegaColumn[] }) {
  const path = usePath();
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  useEffect(() => setOpen(false), [path]);
  // Pull the panel left by however much it would run past the window's right edge (16 px gutter).
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = ref.current?.getBoundingClientRect().left ?? 0;
      const width = panel.current?.offsetWidth ?? 0;
      setShift(Math.min(0, window.innerWidth - 16 - (anchor + width)));
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  const cols = columns.filter((c) => c.links.length || c.promo);
  if (!cols.length) return null;
  const here = (l: MegaLink) => l.prefixes.some((p) => within(path, p));
  // A picture card's page counts too: it is usually the section's landing page (/copy, /earn).
  const active = cols.some((c) => c.links.some(here) || (c.promo && within(path, c.promo.to)));
  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };
  return (
    <div ref={ref} className="relative">
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          'flex items-center gap-1 rounded-full py-2 pr-2 pl-2.5 text-[14px] font-semibold text-nav-fg 2xl:pl-4',
          active || open ? 'bg-nav-pill' : 'opacity-80 hover:opacity-100',
        )}
      >
        {label}
        <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" className={cx('transition-transform', open && 'rotate-180')}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div
          ref={panel}
          role="menu"
          aria-label={label}
          style={{ width: `min(calc(100vw - 32px), ${cols.length * 250}px)`, transform: shift ? `translateX(${shift}px)` : undefined }}
          className="shadow-pop absolute top-12 left-0 z-40 grid auto-cols-fr grid-flow-col divide-x divide-line overflow-hidden rounded-2xl border border-line bg-panel text-fg"
        >
          {cols.map((c) => (
            <div key={c.title} role="group" aria-label={c.title} className="flex min-w-0 flex-col gap-1 p-4 xl:p-5">
              <div className="mb-2 text-[15px] font-semibold">{c.title}</div>
              {c.promo && <PromoCard promo={c.promo} onClick={() => go(c.promo!.to)} className="mb-2" />}
              {c.links.map((l) => (
                <button
                  key={l.to}
                  type="button"
                  role="menuitem"
                  aria-current={here(l) ? 'page' : undefined}
                  onClick={() => go(l.to)}
                  className={cx('-mx-2 flex items-start gap-3 rounded-xl p-2 text-left hover:bg-raised', here(l) && 'bg-raised')}
                >
                  <GlyphBadge name={l.glyph} size="sm" />
                  <span className="min-w-0 pt-0.5">
                    <span className="block text-[14px] font-semibold">{l.label}</span>
                    <span className="block text-[12.5px] text-muted">{l.blurb}</span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
